import { useEffect, useState } from "react";

/**
 * Shared, SSR/storybook-safe environment probes for the PWA layer.
 *
 * Every reader guards `window`/`matchMedia` so the hooks return inert defaults
 * during a static render (Storybook, SSR) and only attach listeners inside
 * `useEffect`. This mirrors the reduced-motion pattern already used by
 * `useCountUp.ts` and `chartSetup.ts`. [task #15]
 */

function matches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

/** True when `prefers-reduced-motion: reduce` (or printing). */
export function prefersReducedMotion(): boolean {
  return matches("(prefers-reduced-motion: reduce)") || matches("print");
}

/**
 * True on touch / coarse-pointer devices or narrow viewports. Used to gate the
 * pull-to-refresh gesture so it never hijacks desktop mouse-wheel scrolling.
 */
export function isCoarsePointer(): boolean {
  return matches("(pointer: coarse)") || matches("(max-width: 768px)");
}

/** True when the app is launched as an installed PWA (standalone display). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari exposes `navigator.standalone`; everyone else uses display-mode.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return matches("(display-mode: standalone)") || iosStandalone === true;
}

/** True on iOS Safari, which has no `beforeinstallprompt` event. */
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as desktop Safari but is touch + Mac platform.
  const iPadOs = navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome/.test(ua);
  return (iDevice || iPadOs) && isSafari;
}

/**
 * Reactive `prefers-reduced-motion` hook. Resolves the value once on mount
 * (inside `useEffect`, so the first render is a safe `false`) and updates live
 * when the OS preference changes.
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);
  return reduced;
}

/** Reactive coarse-pointer / small-screen hook, settled inside `useEffect`. */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const queries = ["(pointer: coarse)", "(max-width: 768px)"].map((q) => window.matchMedia(q));
    const apply = () => setCoarse(queries.some((q) => q.matches));
    apply();
    queries.forEach((q) => q.addEventListener?.("change", apply));
    return () => queries.forEach((q) => q.removeEventListener?.("change", apply));
  }, []);
  return coarse;
}

/** Reactive online/offline hook, settled inside `useEffect`. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(true);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setOnline(navigator.onLine);
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}
